import { Head, Link, router } from "@inertiajs/react"
import { Flame } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { DormantInflectionBand } from "@/components/creator/dormant-inflection-band"
import { AggregateCard } from "@/components/creator/welcome-back/aggregate-card"
import { StreakJeopardyRow } from "@/components/creator/welcome-back/streak-jeopardy-row"
import { WelcomeBackSkeleton } from "@/components/creator/welcome-back/welcome-back-skeleton"
import { MissionCard } from "@/components/mission-card"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import {
  creatorDormancyBannerPath,
  creatorMissionDetailPath,
  creatorWelcomeBackRejoinPath,
} from "@/routes"
import type { WelcomeBackProps } from "@/types/welcome-back"

/**
 * OKS-560 — §21 dormancy re-engagement screen (`/missions/welcome-back`).
 *
 * The before-return loss-aversion moment: a lapsed creator lands here from the
 * `creator.crew.moved_on` notification deep-link. Single scrollable mobile
 * column (402px), top→bottom (UX Spec §2):
 *   1. Warm hero (reuse `<DormantInflectionBand>` — "{BRAND} MISSED YOU" +
 *      "Welcome back, {first}").
 *   2. `<AggregateCard>` — "{brand}'s crew posted {N} times since you've been
 *      away" + PII-floored cluster (count-only, brand-internal).
 *   3. `<StreakJeopardyRow>` — at-risk "🔥 …resets if you miss this drop", or
 *      the broken-streak clean-start line (edge), or nothing (safe streak).
 *   4. Rejoin `<MissionCard>` (focused mode) — filled "Rejoin this mission"
 *      CTA + ghost "Not now". Cover / urgency / social-proof preserved via the
 *      full `collab` wire (UX Spec §2.4 audit).
 *
 * States (UX Spec §3):
 *   - Default  — the four regions above.
 *   - Success  — `rejoined` true → the Rejoin card is replaced by the
 *                "Streak protected" (or "Fresh start" on a reset streak)
 *                celebration + a CTA into the mission flow. CSS-only motion.
 *   - Edge     — reset streak → jeopardy row becomes the clean-start line; the
 *                success copy shifts from "protected" to "fresh start".
 *   - Loading  — `<WelcomeBackSkeleton>` while an error `Try again` reloads.
 *   - Error    — an action navigation failure surfaces a Sonner toast +
 *                re-enables the CTA (mirrors the V1 optimistic-with-error
 *                pattern); a persistent load failure shows an inline retry card.
 *
 * Crew rules: js-routes for every URL, plain Inertia `router` POST/PATCH for
 * the (field-less) Rejoin / Not-now actions, no `localStorage`, CSS-only
 * motion. Brand isolation: every prop is server-scoped to `Current.membership`.
 */
export default function CreatorWelcomeBack({
  hero,
  aggregate,
  streak,
  collab,
  rejoined,
}: WelcomeBackProps) {
  const { t } = useTranslation()
  const [submitting, setSubmitting] = useState(false)
  const [reloading, setReloading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  // A reset (broken) streak means there was nothing to protect — the rejoin
  // STARTS a streak rather than saving one (edge framing, no guilt copy).
  const cleanStart = streak.state === "broken"

  function handleRejoin() {
    setSubmitting(true)
    router.post(
      creatorWelcomeBackRejoinPath(),
      {},
      {
        preserveScroll: true,
        // Success → the server redirects to `?rejoined=<id>` and the Success
        // region renders. A failure surfaces the inline retry card + a toast
        // (UX Spec §3 error); `onFinish` re-enables on any terminal outcome.
        onError: () => {
          setLoadError(true)
          toast.error(t("creator_welcome_back.rejoin.error"))
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function handleNotNow() {
    // "Not now" reuses the shipped dormancy seen-marker (dismisses this window)
    // and redirects to Crew Home. Optimistic; a persist failure re-enables +
    // toasts (mirrors the V1 banner dismiss).
    setSubmitting(true)
    router.patch(
      creatorDormancyBannerPath(),
      {},
      {
        preserveScroll: true,
        onError: () => {
          setLoadError(true)
          toast.error(t("creator_welcome_back.not_now.error"))
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function handleRetry() {
    setReloading(true)
    router.reload({
      onError: () => toast.error(t("creator_welcome_back.error.retry_failed")),
      onFinish: () => {
        setReloading(false)
        setLoadError(false)
      },
    })
  }

  return (
    <AppLayout>
      <Head
        title={t("creator_welcome_back.title", { brand: hero.brand_name })}
      />

      <main
        role="region"
        aria-label={t("creator_welcome_back.region_label")}
        className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-5"
        data-testid="welcome-back"
      >
        {reloading ? (
          <WelcomeBackSkeleton />
        ) : (
          <>
            {/* 1. Warm hero — reuse the shipped dormant-inflection band accent. */}
            <DormantInflectionBand
              brandName={hero.brand_name}
              firstName={hero.first_name}
            />

            {/* 2. AggregateCard — crew posts since you've been away. */}
            <AggregateCard aggregate={aggregate} brandName={hero.brand_name} />

            {loadError ? (
              /* Error — inline retry card (hero already rendered above; the
                 warm entry is never blocked, UX Spec §3). */
              <Card data-testid="welcome-back-error">
                <CardContent className="flex flex-col items-center gap-3 py-6 text-center">
                  <p className="text-sm font-medium">
                    {t("creator_welcome_back.error.body")}
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleRetry}
                    className="min-h-11"
                    data-testid="welcome-back-retry"
                  >
                    {t("creator_welcome_back.error.retry")}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* 3. Streak-jeopardy row (at-risk) / clean-start line (edge). */}
                <StreakJeopardyRow streak={streak} />

                {/* 4. Success celebration OR the Rejoin MissionCard. */}
                {rejoined ? (
                  <Card
                    data-testid="welcome-back-success"
                    className="motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 border-emerald-500/40 bg-emerald-50/50 dark:bg-emerald-950/20"
                  >
                    <CardContent className="flex flex-col items-center gap-3 py-6 text-center">
                      <Flame
                        className="size-8 text-amber-500"
                        aria-hidden="true"
                      />
                      <p className="text-base font-semibold">
                        {cleanStart
                          ? t("creator_welcome_back.success.fresh_start_title")
                          : t("creator_welcome_back.success.protected_title", {
                              count: streak.current_streak_days,
                            })}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {t("creator_welcome_back.success.subtitle")}
                      </p>
                      <Button
                        asChild
                        className="min-h-11 w-full"
                        data-testid="welcome-back-success-cta"
                      >
                        <Link href={creatorMissionDetailPath(collab.id)}>
                          {t("creator_welcome_back.success.cta")}
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <MissionCard
                    collab={collab}
                    ctaState={submitting ? "disabled-progress" : "enabled"}
                    ctaLabel="creator_welcome_back.rejoin.cta"
                    ctaAction={{ onClick: handleRejoin }}
                    secondaryAction={{
                      labelKey: "creator_welcome_back.rejoin.not_now",
                      onClick: handleNotNow,
                    }}
                  />
                )}
              </>
            )}
          </>
        )}
      </main>
    </AppLayout>
  )
}
